import { analyticsMaxDate, analyticsPresetRange } from '@/lib/analyticsDateRange';
import { Download, Loader2, Play, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  AutocompleteFilter,
  Button,
  DateRangePicker,
  DateRangePickerPresetKey,
  buildDateRangePickerPresets,
  toast,
  type DateRangePickerValue,
} from '@shigo-ui/ui';

import { dashboardApiErrorMessage } from '../../../../../lib/apiErrorMessages';
import { downloadBlob } from '../../../../../lib/downloadBlob';
import { dashboardT } from '../../../../../lib/messages';
import {
  useNetworkPerformanceExportMutation,
  useNetworkPerformanceNetworksQuery,
  useNetworkPerformanceQuery,
} from '../../hooks';
import { buildNetworkPerformanceRequest, type NetworkPerformanceRequest } from '../../model';
import { NetworkPerformanceTable } from '../NetworkPerformanceTable';

const presets = buildDateRangePickerPresets(
  [
    DateRangePickerPresetKey.ThisMonth,
    DateRangePickerPresetKey.LastMonth,
    DateRangePickerPresetKey.Last30Days,
    DateRangePickerPresetKey.Last3Months,
    DateRangePickerPresetKey.ThisYear,
  ],
  { endPeriodsYesterday: true },
);

interface NetworkPerformanceDraft {
  dateRange: DateRangePickerValue;
  selectedNetworkIds: string[];
}

export function NetworkPerformancePage() {
  const [draft, setDraft] = useState<NetworkPerformanceDraft>(createInitialDraft);
  const [request, setRequest] = useState<NetworkPerformanceRequest>(() => requestFromDraft(draft));
  const [reportVersion, setReportVersion] = useState(0);
  const networkQuery = useNetworkPerformanceNetworksQuery();
  const reportQuery = useNetworkPerformanceQuery(request);
  const exportMutation = useNetworkPerformanceExportMutation();
  const errorMessage = reportQuery.error ? dashboardApiErrorMessage(reportQuery.error) : undefined;
  const networkOptions = useMemo(
    () => (networkQuery.data ?? []).map((network) => ({ id: network.id, label: network.name })),
    [networkQuery.data],
  );

  async function runReport() {
    const nextRequest = requestFromDraft(draft);
    setReportVersion((current) => current + 1);
    if (sameRequest(request, nextRequest)) {
      await reportQuery.refetch();
      return;
    }
    setRequest(nextRequest);
  }

  async function exportReport() {
    const exportRequest = requestFromDraft(draft);
    try {
      const blob = await exportMutation.mutateAsync(exportRequest);
      downloadBlob(
        blob,
        `network-performance-${exportRequest.dateFrom}-${exportRequest.dateTo}.csv`,
      );
      toast.success(dashboardT('networkPerformance.exportSuccess'));
    } catch {
      // The shared mutation handler presents the API error.
    }
  }

  const controls = (
    <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-3">
      <div className="w-full sm:w-72">
        <AutocompleteFilter
          label={dashboardT('networkPerformance.chooseNetwork')}
          searchPlaceholder={dashboardT('networkPerformance.searchNetworks')}
          options={networkOptions}
          selectedIds={draft.selectedNetworkIds}
          onSelectedIdsChange={(selectedNetworkIds) => {
            setDraft((current) => ({ ...current, selectedNetworkIds }));
          }}
          loading={networkQuery.isLoading}
          disabled={networkQuery.isError}
          icon={<Search className="h-4 w-4" />}
        />
        {networkQuery.isError ? (
          <p className="text-destructive mt-1 text-xs">
            {dashboardT('networkPerformance.networksError')}
          </p>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
        <Button type="button" disabled={reportQuery.isFetching} onClick={() => void runReport()}>
          {reportQuery.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          {dashboardT('networkPerformance.run')}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={
            reportQuery.isFetching ||
            exportMutation.isPending ||
            !(reportQuery.data?.rows.length ?? 0)
          }
          onClick={() => void exportReport()}
        >
          {exportMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {dashboardT('networkPerformance.export')}
        </Button>
        <DateRangePicker
          label={dashboardT('networkPerformance.dateRange')}
          presets={presets}
          value={draft.dateRange}
          maxDate={analyticsMaxDate()}
          onChange={(dateRange) => {
            setDraft((current) => ({ ...current, dateRange }));
          }}
          className="w-full sm:w-auto"
        />
      </div>
    </div>
  );

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 sm:py-6 lg:py-8">
      <NetworkPerformanceTable
        key={reportVersion}
        rows={reportQuery.data?.rows ?? []}
        loading={reportQuery.isFetching}
        errorMessage={errorMessage}
        controls={controls}
      />
    </main>
  );
}

function createInitialDraft(): NetworkPerformanceDraft {
  const dateRange = analyticsPresetRange(DateRangePickerPresetKey.ThisMonth);
  return {
    dateRange,
    selectedNetworkIds: [],
  };
}

function requestFromDraft(draft: NetworkPerformanceDraft) {
  return buildNetworkPerformanceRequest(
    draft.dateRange.dateFrom,
    draft.dateRange.dateTo,
    draft.selectedNetworkIds,
  );
}

function sameRequest(left: NetworkPerformanceRequest, right: NetworkPerformanceRequest) {
  return JSON.stringify(left) === JSON.stringify(right);
}
